import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowLeft, Shovel } from 'lucide-react';
import './ServiceDetail.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_2fdbb258-2fe3-4bcb-af8e-a32c1bf06202/artifacts/xuzlwsfh_WhatsApp%20Image%202026-03-21%20at%2015.32.59.jpeg';

const ErdarbeitenPage = () => {
  const [heroImage, setHeroImage] = useState(DEFAULT_IMAGE);

  useEffect(() => {
    document.title = 'Erdarbeiten & Baggerarbeiten | Nessenius Garten & Landschaftsbau';
    window.scrollTo(0, 0);

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Professionelle Erdarbeiten & Baggerarbeiten in Göttingen, Nörten-Hardenberg und Umgebung. Minibagger mit Fahrer, Aushub, Fundamente, Drainage. Festpreise. Jetzt anrufen: 0176-55669090');

    // Fetch image from API
    const fetchImage = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/homepage-images`);
        if (response.ok) {
          const data = await response.json();
          if (data.erdarbeiten?.image_url) {
            setHeroImage(data.erdarbeiten.image_url);
          }
        }
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    };
    fetchImage();
  }, []);

  return (
    <div className="service-detail-page erdarbeiten-page">
      {/* Hero */}
      <section className="service-detail-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="service-detail-overlay"></div>
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={18} />
            Zurück zur Startseite
          </Link>
          <div className="service-detail-icon">
            <Shovel size={40} />
          </div>
          <h1>Erdarbeiten & Gartenbau</h1>
          <p className="service-detail-subtitle">
            Mit Minibagger und Kipper führen wir Aushub, Geländevorbereitung und Bauarbeiten zuverlässig und sauber aus.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-detail-content">
        <div className="container">
          <div className="service-detail-intro">
            <h2>Was wir für Sie erledigen</h2>
            <p>
              Wir übernehmen Erdarbeiten und kleinere Bauprojekte rund um Haus und Garten.
              Ob Aushub für ein Gartenhaus, Geländemodellierung oder Drainagearbeiten – 
              wir bringen die nötige Erfahrung und das passende Gerät mit.
            </p>
          </div>

          <div className="service-detail-grid">
            <div className="service-detail-card">
              <h3>Bagger- & Erdarbeiten</h3>
              <ul>
                <li><CheckCircle size={18} /> Baggerarbeiten mit Minibagger</li>
                <li><CheckCircle size={18} /> Erdarbeiten aller Art</li>
                <li><CheckCircle size={18} /> Aushub / Auskoffern</li>
                <li><CheckCircle size={18} /> Erdtransport mit Kipper</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Baugruben & Fundamente</h3>
              <ul>
                <li><CheckCircle size={18} /> Baugruben für Gartenhaus</li>
                <li><CheckCircle size={18} /> Baugruben für Pool / Teich</li>
                <li><CheckCircle size={18} /> Fundamentvorbereitung</li>
                <li><CheckCircle size={18} /> Keller- und Schachtarbeiten</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Geländearbeiten</h3>
              <ul>
                <li><CheckCircle size={18} /> Geländevorbereitung</li>
                <li><CheckCircle size={18} /> Planieren & Einebnen</li>
                <li><CheckCircle size={18} /> Boden abtragen & auffüllen</li>
                <li><CheckCircle size={18} /> Hangabfangungen</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Mauern & Befestigungen</h3>
              <ul>
                <li><CheckCircle size={18} /> Winkelstützen setzen</li>
                <li><CheckCircle size={18} /> Kleine Mauern (Schalstein)</li>
                <li><CheckCircle size={18} /> Randsteine setzen</li>
                <li><CheckCircle size={18} /> Vorbereitung für Pflaster</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Drainage & Entwässerung</h3>
              <ul>
                <li><CheckCircle size={18} /> Drainage verlegen</li>
                <li><CheckCircle size={18} /> Sickerschacht im Garten</li>
                <li><CheckCircle size={18} /> Versickerungsanlagen</li>
                <li><CheckCircle size={18} /> Regenwassermanagement</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Entsorgung</h3>
              <ul>
                <li><CheckCircle size={18} /> Erdaushub entsorgen</li>
                <li><CheckCircle size={18} /> Bauschutt abfahren</li>
                <li><CheckCircle size={18} /> Wurzelstöcke entfernen</li>
                <li><CheckCircle size={18} /> Rückbau / Abbruch klein</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-cta">
        <div className="container">
          <h2>Projekt anfragen</h2>
          <p>Erzählen Sie uns von Ihrem Vorhaben. Wir schauen es uns an und machen Ihnen ein faires Angebot.</p>
          <div className="cta-buttons">
            <a href="tel:+4917655669090" className="btn-cta">
              <Phone size={18} />
              0176 - 556 690 90
            </a>
            <Link to="/kontakt" className="btn-secondary">
              Kontaktformular
            </Link>
          </div>
        </div>
      </section>

      {/* Local SEO Links */}
      <section className="service-local-links">
        <div className="container">
          <h2>Erdarbeiten in Ihrer Nähe</h2>
          <div className="local-links-grid">
            {[
              ['goettingen', 'Göttingen'], ['northeim', 'Northeim'], ['einbeck', 'Einbeck'],
              ['duderstadt', 'Duderstadt'], ['noerten-hardenberg', 'Nörten-Hardenberg'], ['bovenden', 'Bovenden'],
              ['hardegsen', 'Hardegsen'], ['osterode', 'Osterode'], ['herzberg', 'Herzberg'],
              ['bad-lauterberg', 'Bad Lauterberg'], ['moringen', 'Moringen'], ['uslar', 'Uslar'],
              ['hann-muenden', 'Hann. Münden'], ['gieboldehausen', 'Gieboldehausen'], ['dransfeld', 'Dransfeld'],
              ['hattorf', 'Hattorf'], ['dassel', 'Dassel'], ['katlenburg-lindau', 'Katlenburg-Lindau'],
            ].map(([slug, name]) => (
              <Link key={slug} to={`/erdarbeiten/${slug}`} className="local-link-item">
                Erdarbeiten {name}
              </Link>
            ))}
          </div>
          <div className="local-links-alt">
            <h3>Auch gefunden unter:</h3>
            <div className="local-links-grid">
              {[
                ['baggerarbeiten/goettingen', 'Baggerarbeiten Göttingen'],
                ['minibagger/goettingen', 'Minibagger Göttingen'],
                ['aushub/goettingen', 'Aushub Göttingen'],
                ['baggerarbeiten/northeim', 'Baggerarbeiten Northeim'],
                ['minibagger/northeim', 'Minibagger Northeim'],
                ['baggerarbeiten/duderstadt', 'Baggerarbeiten Duderstadt'],
                ['tiefbau/goettingen', 'Tiefbau Göttingen'],
                ['minibagger/einbeck', 'Minibagger Einbeck'],
              ].map(([path, label]) => (
                <Link key={path} to={`/${path}`} className="local-link-item alt">
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ErdarbeitenPage;
